import {isFunction} from 'shared/utils/custom-utils';
import type {Identified} from 'typings/portaro.be.types';
import type {DefinedAction} from '../../services/defined-action-performer';
import type DefinedActionPerformer from '../../services/defined-action-performer';

export type IsLoggedFunction = (...args: any[]) => boolean;
export type OnRequireAuthClickFunction = (menuItem?: MenuItem, $event?: MouseEvent, ...args: any[]) => void;

export interface MenuItem extends Identified<Record<string, unknown>> {
    text: string;
    requiresAuth: boolean;
    type: 'user' | null;
    action: DefinedAction
    submenu: MenuItem[];
}

/**
 * @ngdoc service
 * @name mainMenuService
 * @module portaro.components.main-menu
 *
 * @description
 * Service for {@link component:kpMainMenu main menu}
 */
export interface MainMenuService {
    performAction(action: DefinedAction);

    isLogged(): boolean;

    requireAuthClick(menuItem: MenuItem, $event: MouseEvent);
}

/**
 * @ngdoc provider
 * @name mainMenuServiceProvider
 * @module portaro.components.main-menu
 *
 * @description
 * Provider for setting  custom functions for correct functioning of {@link component:kpMainMenu}
 *
 * ## example
 * ```js
 * angular.config(
 *      ['mainMenuServiceProvider', (mainMenuServiceProvider) => {
 *           mainMenuServiceProvider.setIsLoggedFunction((myAuthService) => {
 *               return myAuthService.isLogged();
 *           });
 *
 *           mainMenuServiceProvider.setOnRequireAuthClick((menuItem) => {
 *              alert(`For access on page ${menuItem.text}, you must be logged in.`);
 *           });
 *
 *           mainMenuServiceProvider.setSearchFunction((searchPhrase) => {
 *              console.log('Search: ', searchPhrase);
 *           });
 *
 *           mainMenuServiceProvider.setAutocompleteSearchFunction((searchPhrase, $http) => {
 *              return $http.get('/api/autocomplete/searched-terms', {infix: searchPhrase});
 *           });
 *      }]
 * );
 * ```
 */
export default class MainMenuServiceProvider {
    public static providerName = 'mainMenuService';

    public readonly $get: ($injector: angular.auto.IInjectorService, definedActionPerformer: DefinedActionPerformer) => MainMenuService;

    private getLoggedFunction: IsLoggedFunction = () => true;

    private onRequireAuthClickFunction: OnRequireAuthClickFunction = () => null;

    constructor() {
        this.$get = /*@ngInject*/ ($injector: angular.auto.IInjectorService, definedActionPerformer: DefinedActionPerformer) => this.createMainMenuService($injector, definedActionPerformer);
    }

    /**
     * @ngdoc method
     * @name mainMenuServiceProvider#setIsLoggedFunction
     *
     * @description
     * Set function which returns boolean value of login status. Default returns `true`
     *
     * @param {function(*=): boolean} loggedFunction Function, which returns login status. Default is `true`. Function supports dependency injection through function arguments
     */
    public setIsLoggedFunction(loggedFunction: IsLoggedFunction) {
        if (isFunction(loggedFunction)) {
            this.getLoggedFunction = loggedFunction;
        } else {
            throw new TypeError(`Invalid argument type. Function required, got: ${loggedFunction}`);
        }
    }

    /**
     * @ngdoc method
     * @name mainMenuServiceProvider#setOnRequireAuthClick
     *
     * @description
     * Set function which is triggered when user clicks on menu item, which requires logged user.
     *
     * @param {function(*)} onRequireAuthClickFunction Function, which is triggered if user is not logged in and clicks on menu item, which requires authentication.
     * Function supports dependency injection through function arguments. Selected menu item and fired event are accessible via `menuItem` and `$event` arguments.
     */
    public setOnRequireAuthClick(onRequireAuthClickFunction: OnRequireAuthClickFunction) {
        if (isFunction(onRequireAuthClickFunction)) {
            this.onRequireAuthClickFunction = onRequireAuthClickFunction;
        } else {
            throw new TypeError(`Invalid argument type. Function required, get: ${onRequireAuthClickFunction}`);
        }
    }

    private createMainMenuService($injector: angular.auto.IInjectorService, definedActionPerformer: DefinedActionPerformer): MainMenuService {
        return {
            performAction: (action: DefinedAction) => definedActionPerformer.performAction(action),
            isLogged: () => Boolean($injector.invoke(this.getLoggedFunction, this)),
            requireAuthClick: (menuItem: MenuItem, $event: MouseEvent) => $injector.invoke(this.onRequireAuthClickFunction, this, {menuItem, $event})
        };
    }
}
